import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import { BsQrCode } from 'react-icons/bs';
import Tooltip from '@material-ui/core/Tooltip';
import { PDFDocument, rgb } from 'pdf-lib';
import QRCode from 'qrcode';
import { download } from 'utilities/file';
import fontkit from 'pdf-fontkit';
import { formatAddress, getShortIDFromUUID } from 'utilities/format';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { useCache } from 'CacheProvider';
import { Storage } from 'aws-amplify';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';

export default function DownloadElderQRCodeButton({ data, displayData, as, ...props }) {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const { selectedUserGroupParams } = useCache();
  const qrCodeSettings = selectedUserGroupParams?.clientData?.facilitySettings?.qrCodeSettings || {};
  const { showElderName, documentTitle, documentTitleImageS3Keys } = qrCodeSettings;

  const currentUrl = window.location.origin;
  const handleButtonClick = async () => {
    setOpen(true);
    setProgress(0);
    setStatus('準備中...');

    // data 是從ElderTable傳進來的Ref，所以需要用這樣的方式取得資料
    const getData = () => {
      if (data && typeof data === 'object' && 'current' in data) {
        return data.current || [];
      }
      return data || [];
    };

    let filteredData = [...getData()];
    if (displayData) {
      filteredData = filteredData.filter((elder) => displayData.some(({ data }) => data[0] === elder.id));
    }
    const sortedElders = filteredData.sort((a, b) => {
      const addr1 = formatAddress(a.address);
      const addr2 = formatAddress(b.address);
      return (addr1 > addr2 ? 1 : -1);
    });

    try {
      const pdfDoc = await PDFDocument.create();
      pdfDoc.registerFontkit(fontkit);

      setStatus('載入字體...');
      const [regularFontResponse, boldFontResponse] = await Promise.all([
        fetch('/fonts/NotoSansTC-Regular.ttf'),
        fetch('/fonts/NotoSansTC-Bold.ttf'),
      ]);
      const [regularFontBytes, boldFontBytes] = await Promise.all([
        regularFontResponse.arrayBuffer(),
        boldFontResponse.arrayBuffer(),
      ]);
      const regularFont = await pdfDoc.embedFont(regularFontBytes, { subset: true });
      const boldFont = await pdfDoc.embedFont(boldFontBytes, { subset: true });

      setProgress(10);
      setStatus('載入背景圖片...');
      const qrCodeBGImage = await fetch('/images/qr_code_bg.png');
      const qrCodeBGImageBuffer = await qrCodeBGImage.arrayBuffer();
      const qrCodeBGImagePng = await pdfDoc.embedPng(qrCodeBGImageBuffer);

      setProgress(20);
      const mmToPoints = (mm) => mm * 72 / 25.4;
      const pageSize = [841.89, 595.28];
      const qrSize = mmToPoints(80);
      const pageWidth = pageSize[0];
      const halfPageWidth = pageWidth / 2;

      const drawQRCodeSection = async (page, elder, xOffset, height) => {
        const url = `${currentUrl}/pod/${elder.id}`;
        const caseNumber = elder.caseNumber || getShortIDFromUUID(elder.id);
        const qrPng = await QRCode.toDataURL(url, {
          width: qrSize,
          margin: 1,
        });

        const qrCodeBGImageWidth = halfPageWidth;
        const qrCodeBGImageHeight = qrCodeBGImageWidth * (qrCodeBGImagePng.height / qrCodeBGImagePng.width);
        page.drawImage(qrCodeBGImagePng, {
          x: xOffset + (halfPageWidth - qrCodeBGImageWidth) / 2,
          y: 0,
          width: qrCodeBGImageWidth,
          height: qrCodeBGImageHeight,
        });

        if (documentTitleImageS3Keys?.length > 0) {
          const s3Key = documentTitleImageS3Keys[0];
          const uri = await Storage.get(s3Key);
          const titleImage = await fetch(uri);
          const titleImageBuffer = await titleImage.arrayBuffer();
          const extension = s3Key.split('.').pop().toLowerCase();
          let titleEmbedImage;
          if (extension === 'png') {
            titleEmbedImage = await pdfDoc.embedPng(titleImageBuffer);
          } else if (extension === 'jpg' || extension === 'jpeg') {
            titleEmbedImage = await pdfDoc.embedJpg(titleImageBuffer);
          } else {
            throw new Error(`不支援的標題圖片格式: ${extension}`);
          }
          const titleWidth = mmToPoints(80);
          const titleHeight = titleWidth * (titleEmbedImage.height / titleEmbedImage.width);
          page.drawImage(titleEmbedImage, {
            x: xOffset + (halfPageWidth - titleWidth) / 2,
            y: height - 130,
            width: titleWidth,
            height: titleHeight,
          });
        }

        const pngImage = await pdfDoc.embedPng(qrPng);
        page.drawImage(pngImage, {
          x: xOffset + (halfPageWidth - qrSize) / 2,
          y: (height - qrSize) / 2 - 40,
          width: qrSize,
          height: qrSize,
        });

        const text = documentTitle || '營養送餐服務';
        const textWidth = boldFont.widthOfTextAtSize(text, 24);
        page.drawText(text, {
          x: xOffset + (halfPageWidth - textWidth) / 2,
          y: height - 165,
          size: 24,
          color: rgb(0, 0, 0),
          font: boldFont,
        });

        const text2 = '請掃描下方QR Code確認送餐資訊';
        const text2Width = regularFont.widthOfTextAtSize(text2, 16);
        page.drawText(text2, {
          x: xOffset + (halfPageWidth - text2Width) / 2,
          y: height - 200,
          size: 16,
          color: rgb(0, 0, 0),
          font: regularFont,
        });

        const text3 = showElderName ? elder.name : `服務編號: ${caseNumber}`;
        const text3Width = regularFont.widthOfTextAtSize(text3, 16);
        page.drawText(text3, {
          x: xOffset + (halfPageWidth - text3Width) / 2,
          y: height - 480,
          size: 16,
          color: rgb(0, 0, 0),
          font: regularFont,
        });
      };

      setStatus('生成 QR Code...');
      const totalPages = Math.ceil(sortedElders.length / 2);
      for (let i = 0; i < sortedElders.length; i += 2) {
        const page = pdfDoc.addPage(pageSize);
        const { height } = page.getSize();
        await drawQRCodeSection(page, sortedElders[i], 0, height);
        if (i + 1 < sortedElders.length) {
          await drawQRCodeSection(page, sortedElders[i + 1], halfPageWidth, height);
        }
        setProgress(20 + (i / 2 / totalPages * 60));
      }

      if (sortedElders.length > 1) {
        setStatus('生成 Excel 檔案...');
        const zip = new JSZip();
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('送餐對象清單');

        worksheet.columns = [
          { header: '案號(服務編號)', key: 'caseNumber', width: 15 },
          { header: '姓名', key: 'name', width: 20 },
          { header: '地址', key: 'address', width: 40 },
        ];

        sortedElders.forEach((elder) => {
          worksheet.addRow({
            name: elder.name,
            address: formatAddress(elder.address),
            caseNumber: elder.caseNumber || getShortIDFromUUID(elder.id),
          });
        });

        setProgress(85);
        setStatus('壓縮檔案...');
        const excelBuffer = await workbook.xlsx.writeBuffer();
        zip.file('送餐對象清單.xlsx', excelBuffer, { binary: true });

        const pdfBytes = await pdfDoc.save();
        zip.file('送餐對象QR Code.pdf', pdfBytes, { binary: true });
        const zipContent = zip.generate({ type: 'blob' });

        setProgress(95);
        setStatus('下載中...');
        download('送餐對象資料.zip', zipContent);
      } else {
        setProgress(90);
        setStatus('下載中...');
        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        download(`送餐對象QR Code.pdf`, blob);
      }

      setProgress(100);
      setStatus('完成！');
      setTimeout(() => {
        setOpen(false);
      }, 1000);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setStatus('發生錯誤，請重試');
      setTimeout(() => {
        setOpen(false);
      }, 5000);
    }
  };

  const renderButton = () => {
    if (as === 'button') {
      return (
        <Button
          onClick={handleButtonClick}
          startIcon={<BsQrCode />}
          {...props}
        >
          下載QR Code
        </Button>
      );
    }
    return (
      <Tooltip title={'下載QR Code'}>
        <IconButton
          size={'small'}
          onClick={handleButtonClick}
          {...props}
        >
          <BsQrCode />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <React.Fragment>
      {renderButton()}
      <Dialog
        open={open}
        onClose={() => {}}
        PaperProps={{
          style: {
            width: '400px',
            maxWidth: '90vw',
          },
        }}
      >
        <DialogTitle>QR Code 下載中...</DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
          <Typography variant="body2" color="textSecondary">
            {status}
          </Typography>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

DownloadElderQRCodeButton.propTypes = {
  data: PropTypes.array,
  displayData: PropTypes.array,
  as: PropTypes.string,
};
